/**
 * Shopacado Discount Table Block
 * Initializes discount table blocks on product pages
 */
(function() {
  'use strict';

  // Cache for original product price - stored on window to persist across script re-runs
  // This prevents price recalculation when cart operations cause DOM updates
  if (typeof window.shopacadoCachedProductPrice === 'undefined') {
    window.shopacadoCachedProductPrice = null;
  }

  /**
   * Inject CSS styles for the discount table
   */
  function injectStyles() {
    // Check if styles already injected
    if (document.getElementById('shopacado-table-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'shopacado-table-styles';
    style.textContent = `
      /* Hide table until JavaScript initialization completes */
      .shopacado-discount-table-block:not([data-shopacado-initialized="true"]) {
        opacity: 0 !important;
        visibility: hidden !important;
      }

      .shopacado-discount-table-block[data-shopacado-initialized="true"] {
        opacity: 1 !important;
        visibility: visible !important;
        transition: opacity 0.2s ease-in !important;
      }

      /* Loading spinner styles */
      .shopacado-table-loading {
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
        min-height: 200px !important;
        padding: 40px 20px !important;
        background: #fff !important;
        border-radius: 10px !important;
        transition: opacity 0.2s ease-out !important;
      }

      .shopacado-discount-table-block[data-shopacado-initialized="true"] .shopacado-table-loading {
        opacity: 0 !important;
        visibility: hidden !important;
        position: absolute !important;
        pointer-events: none !important;
      }

      /* Spinner animation */
      .shopacado-spinner {
        width: 40px !important;
        height: 40px !important;
        border: 4px solid #A9D9C5 !important;
        border-top-color: #0F6B6B !important;
        border-radius: 50% !important;
        animation: shopacado-spin 0.8s linear infinite !important;
      }

      @keyframes shopacado-spin {
        to { transform: rotate(360deg); }
      }

      .shopacado-loading-text {
        margin-top: 16px !important;
        color: #0F6B6B !important;
        font-size: 14px !important;
        font-weight: 600 !important;
      }

      /* Shopacado Discount Table Styles */
      div.shopacado-discount-table-block > h4 {
        text-align: center !important;
        background: #759c98 !important;
        color: white !important;
        font-weight: 700 !important;
        height: 30px !important;
        line-height: 1.6 !important;
        font-size: 16px !important;
        border-top-left-radius: 5px !important;
        border-top-right-radius: 5px !important;
      }

      table.shopacado-discount-table {
        table-layout: fixed !important;
        width: 100% !important;
      }

      table.shopacado-discount-table th {
        background: black !important;
        color: white !important;
        font-weight: 700 !important;
        font-size: 13px !important;
        height: 30px !important;
        text-align: center !important;
      }

      table.shopacado-discount-table th:first-child {
        border-bottom-left-radius: 5px !important;
      }

      table.shopacado-discount-table th:last-child {
        border-bottom-right-radius: 5px !important;
      }

      table.shopacado-discount-table td {
        text-align: center !important;
        font-size: 14px !important;
        font-weight: 600 !important;
        height: 30px !important;
        color: black !important;
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Apply inline styles to h4 element as backup
   * @param {HTMLElement} element - The discount table block element
   */
  function applyInlineStyles(element) {
    const h4 = element.querySelector('h4');
    if (h4) {
      // Always set text to "Buy More, Pay Less" regardless of original content
      h4.textContent = 'Buy More, Pay Less';

      h4.style.textAlign = 'center';
      h4.style.background = '#0F6B6B';
      h4.style.color = 'white';
      h4.style.fontWeight = '700';
      h4.style.height = '30px';
      h4.style.lineHeight = '1.6';
      h4.style.fontSize = '16px';
      h4.style.borderTopLeftRadius = '5px';
      h4.style.borderTopRightRadius = '5px';
    }

    const table = element.querySelector('.shopacado-discount-table');
    if (table) {
      table.style.tableLayout = 'fixed';
      table.style.width = '100%';
    }
  }

  /**
   * Extract product price from the page
   * @returns {number|null} Product price in cents, or null if not found
   */
  function getProductPrice() {
    // Return cached price if already retrieved (prevents cart update issues)
    if (window.shopacadoCachedProductPrice !== null) {
      return window.shopacadoCachedProductPrice;
    }

    let price = null;

    // ONLY use static sources that won't change with cart operations

    // Try 1: Shopify product JSON (most reliable, static product data)
    const productJson = document.querySelector('[data-product-json], script[type="application/json"][data-product-json], #ProductJson-product-template');
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        if (product && product.price) {
          price = parseFloat(product.price);
        } else if (product && product.selected_or_first_available_variant && product.selected_or_first_available_variant.price) {
          price = parseFloat(product.selected_or_first_available_variant.price);
        }
      } catch (e) {
        // JSON parse failed, continue to fallback
      }
    }

    // Try 2: meta tag with product price (static, set on page load)
    if (price === null) {
      const metaPrice = document.querySelector('meta[property="product:price:amount"]');
      if (metaPrice && metaPrice.content) {
        // Meta price is usually in dollars/pounds, convert to cents
        price = parseFloat(metaPrice.content) * 100;
      }
    }

    // Try 3: data-cv-price from MAIN product (not upsells/cards)
    // Look in main product containers first to avoid getting upsell prices
    if (price === null) {
      // Try main product containers first
      const mainProductSelectors = [
        '.product-info .js-prod-btn-group[data-cv-price]',
        '.product__info-wrapper .js-prod-btn-group[data-cv-price]',
        '#MainProduct .js-prod-btn-group[data-cv-price]',
        '.product-single .js-prod-btn-group[data-cv-price]',
        'form[action*="/cart/add"] .js-prod-btn-group[data-cv-price]'
      ];

      for (const selector of mainProductSelectors) {
        const btnGroup = document.querySelector(selector);
        if (btnGroup && btnGroup.dataset.cvPrice) {
          price = parseFloat(btnGroup.dataset.cvPrice);
          break;
        }
      }

      // Fallback: find the first one (original behavior)
      if (price === null) {
        const btnGroup = document.querySelector('.js-prod-btn-group[data-cv-price]');
        if (btnGroup && btnGroup.dataset.cvPrice) {
          price = parseFloat(btnGroup.dataset.cvPrice);
        }
      }
    }

    // Cache the price for future calls (survives cart updates - never re-read after this)
    if (price !== null) {
      window.shopacadoCachedProductPrice = price;
    }

    return price;
  }

  /**
   * Extract VAT percentage from the page
   * @returns {number} VAT percentage
   */
  function getVATPercentage() {
    // Try: data-vatrate attribute on product-info
    const productInfo = document.querySelector('.product-info[data-vatrate]');
    if (productInfo && productInfo.dataset.vatrate) {
      const vatMultiplier = parseFloat(productInfo.dataset.vatrate);
      // Convert multiplier (1.20) to percentage (20)
      return (vatMultiplier - 1) * 100;
    }

    // Default fallback
    return 20;
  }

  /**
   * Enrich block element with required data attributes
   * @param {HTMLElement} element - The discount table block element
   * @returns {boolean} True if successfully enriched, false otherwise
   */
  function enrichBlockWithData(element) {
    // Always get price from static sources (product JSON, meta tag) - never from server attribute
    // This ensures the price never changes regardless of cart operations
    const productPrice = getProductPrice();
    const vatPercentage = getVATPercentage();

    if (!productPrice) {
      return false;
    }

    // Apply data attributes (always use static price, ignore any server-rendered value)
    element.dataset.productPriceIncVat = productPrice;
    element.dataset.vatPercentage = vatPercentage;

    return true;
  }

  /**
   * Format price using Shopify money format
   * @param {number} cents - Price in cents
   * @param {string} format - Money format template (e.g., "£{{amount}}")
   * @returns {string} Formatted price string
   */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const value = (cents / 100).toFixed(2);
    const placeholderRegex = /\{\{\s*amount\s*\}\}/;

    return format.replace(placeholderRegex, value);
  }

  /**
   * Initialize a single discount table block
   * @param {HTMLElement} element - The discount table block element
   */
  function initDiscountTableBlock(element) {
    try {
      // Check if already initialized to prevent duplicate columns
      if (element.dataset.shopacadoInitialized === 'true') {
        return;
      }

      // Get the table element
      const table = element.querySelector('.shopacado-discount-table');
      if (!table) {
        return;
      }

      // Additional check: if table already has more than 3 columns, it's already initialized
      const headerCells = table.querySelectorAll('thead th');
      if (headerCells.length > 3) {
        element.dataset.shopacadoInitialized = 'true';
        return;
      }

      // Apply inline styles as backup
      applyInlineStyles(element);

      // First, enrich the element with data attributes from the page
      const enriched = enrichBlockWithData(element);
      if (!enriched) {
        return;
      }

      // Get data attributes from block
      const basePriceIncVat = parseFloat(element.dataset.productPriceIncVat);
      const vatPercentage = parseFloat(element.dataset.vatPercentage);

      // Validate required data
      if (!basePriceIncVat || !vatPercentage) {
        return;
      }

      // Calculate VAT multiplier (e.g., 1.20 for 20% VAT)
      const vatMultiplier = 1 + (vatPercentage / 100);

      // Add column headers
      const thead = table.querySelector('thead tr');
      if (thead) {
        const priceIncVatHeader = document.createElement('th');
        priceIncVatHeader.textContent = 'Price (inc. VAT)';

        // Apply inline styles to header
        priceIncVatHeader.style.background = '#A9D9C5';
        priceIncVatHeader.style.color = '#0F6B6B';
        priceIncVatHeader.style.fontWeight = '700';
        priceIncVatHeader.style.fontSize = '13px';
        priceIncVatHeader.style.height = '30px';
        priceIncVatHeader.style.textAlign = 'center';

        thead.appendChild(priceIncVatHeader);

        const priceExcVatHeader = document.createElement('th');
        priceExcVatHeader.textContent = 'Price (ex. VAT)';

        // Apply inline styles to header
        priceExcVatHeader.style.background = '#A9D9C5';
        priceExcVatHeader.style.color = '#0F6B6B';
        priceExcVatHeader.style.fontWeight = '700';
        priceExcVatHeader.style.fontSize = '13px';
        priceExcVatHeader.style.height = '30px';
        priceExcVatHeader.style.textAlign = 'center';
        priceExcVatHeader.style.borderBottomRightRadius = '5px';

        thead.appendChild(priceExcVatHeader);
      }

      // Add price cells to each row
      const rows = table.querySelectorAll('tbody tr');
      rows.forEach((row) => {
        const cells = row.querySelectorAll('td');
        if (cells.length < 2) return;

        // Get quantity cell to check if this is an inquiry row
        const quantityCell = cells[0];
        const quantityText = quantityCell ? quantityCell.textContent.trim() : '';
        const isInquiryRow = quantityText.includes('30') || (quantityText.includes('+') && parseInt(quantityText) >= 30);

        if (isInquiryRow) {
          // This is a high-quantity inquiry row
          // Remove existing cells (Quantity, Discount, Save)
          if (cells[0]) cells[0].remove();
          if (cells[1]) cells[1].remove();
          if (cells[2]) cells[2].remove();

          // Create single cell spanning all 5 columns with inquiry link
          const inquiryCell = document.createElement('td');
          inquiryCell.setAttribute('colspan', '5');
          inquiryCell.className = 'shopacado-custom-table-val';
          inquiryCell.innerHTML = '<a href="tel:01279460460" style="color: #0F6B6B; cursor: pointer;">Need More? Call Us</a>';

          // Apply inline styles
          inquiryCell.style.fontSize = '14px';
          inquiryCell.style.fontWeight = '600';
          inquiryCell.style.height = '30px';
          inquiryCell.style.textAlign = 'center';

          row.appendChild(inquiryCell);

        } else {
          // Normal price calculation row
          // Find the discount cell (has data-shopacado-discount-val attribute)
          let discountPercent = 0;
          let moneyFormat = '£{{amount}}'; // Default format

          for (let cell of cells) {
            if (cell.hasAttribute('data-shopacado-discount-val')) {
              discountPercent = parseFloat(cell.dataset.shopacadoDiscountVal) || 0;
              moneyFormat = cell.dataset.moneyFormat || moneyFormat;
              break;
            }
          }

          // Calculate prices
          // Price Inc VAT = base price × (1 - discount%)
          // Note: basePriceIncVat is already in cents
          const priceIncVat = basePriceIncVat * (1 - discountPercent / 100);

          // Price Exc VAT = Price Inc VAT ÷ VAT multiplier
          const priceExcVat = priceIncVat / vatMultiplier;

          // Round to nearest cent (already in cents, no conversion needed)
          const priceIncVatCents = Math.round(priceIncVat);
          const priceExcVatCents = Math.round(priceExcVat);

          // Create and append Price (inc. VAT) cell
          const priceIncVatCell = document.createElement('td');
          priceIncVatCell.className = 'shopacado-custom-table-val';
          priceIncVatCell.textContent = formatMoney(priceIncVatCents, moneyFormat);

          // Apply inline styles to cell
          priceIncVatCell.style.fontSize = '14px';
          priceIncVatCell.style.fontWeight = '600';
          priceIncVatCell.style.height = '30px';
          priceIncVatCell.style.textAlign = 'center';

          row.appendChild(priceIncVatCell);

          // Create and append Price (ex. VAT) cell
          const priceExcVatCell = document.createElement('td');
          priceExcVatCell.className = 'shopacado-custom-table-val';
          priceExcVatCell.textContent = formatMoney(priceExcVatCents, moneyFormat);

          // Apply inline styles to cell
          priceExcVatCell.style.fontSize = '14px';
          priceExcVatCell.style.fontWeight = '600';
          priceExcVatCell.style.height = '30px';
          priceExcVatCell.style.textAlign = 'center';

          row.appendChild(priceExcVatCell);
        }
      });

      // Mark as initialized to prevent re-processing
      element.dataset.shopacadoInitialized = 'true';

    } catch (error) {
      // Silently fail - don't break the page
    }
  }

  /**
   * Find and initialize all discount table blocks
   */
  function init() {
    // Inject CSS styles first
    injectStyles();

    const blocks = document.querySelectorAll('.shopacado-discount-table-block');

    if (blocks.length === 0) {
      return;
    }

    blocks.forEach((block) => {
      // Inject loading spinner if it doesn't already exist
      if (!block.querySelector('.shopacado-table-loading')) {
        const loadingDiv = document.createElement('div');
        loadingDiv.className = 'shopacado-table-loading';
        loadingDiv.innerHTML = `
          <div class="shopacado-spinner"></div>
          <p class="shopacado-loading-text">Loading discount table...</p>
        `;

        // Insert as first child (before h4 and table)
        block.insertBefore(loadingDiv, block.firstChild);
      }

      initDiscountTableBlock(block);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded, run immediately
    init();
  }
})();
